import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import {
  Bell,
  CheckCheck,
  AlertTriangle,
  Info,
  ShieldAlert,
  MessageSquare,
  CheckCircle2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '@/features/auth/useAuth';
import { cn } from '@/lib/cn';
import { realtime } from '@/lib/ws';

import { fetchNotifications, fetchUnreadCount, markAllRead, type NotificationRow } from './api';

function notifIcon(type: string) {
  if (type.includes('alert') || type.includes('flag'))
    return <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />;
  if (type.includes('query') || type.includes('message'))
    return <MessageSquare className="h-3.5 w-3.5 text-blue-500" />;
  if (type.includes('resolved') || type.includes('complete'))
    return <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />;
  if (type.includes('critical') || type.includes('error'))
    return <ShieldAlert className="h-3.5 w-3.5 text-red-500" />;
  return <Info className="h-3.5 w-3.5 text-gray-400" />;
}

function notifIconBg(type: string): string {
  if (type.includes('alert') || type.includes('flag')) return '#fffbeb';
  if (type.includes('query') || type.includes('message')) return '#eff6ff';
  if (type.includes('resolved') || type.includes('complete')) return '#f0fdf4';
  if (type.includes('critical') || type.includes('error')) return '#fef2f2';
  return '#f9fafb';
}

function NotifItem({ n }: { n: NotificationRow }) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 px-4 py-3 transition-colors hover:bg-gray-50',
        !n.is_read && 'bg-amber-50/40',
      )}
    >
      <div
        className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl"
        style={{ background: notifIconBg(n.type), border: '1px solid rgba(0,0,0,0.04)' }}
      >
        {notifIcon(n.type)}
      </div>
      <div className="min-w-0 flex-1">
        <p className={cn('text-[12.5px] leading-tight text-gray-800', !n.is_read && 'font-semibold')}>
          {n.title}
        </p>
        {n.body && (
          <p className="mt-0.5 line-clamp-2 text-[11.5px] text-gray-400">{n.body}</p>
        )}
        <p className="mt-1 text-[10.5px] text-gray-300">
          {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
        </p>
      </div>
      {!n.is_read && (
        <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-amber-400" />
      )}
    </div>
  );
}

export function NotificationBell() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const count = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: fetchUnreadCount,
    enabled: !!me,
  });
  const list = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => fetchNotifications(20),
    enabled: open,
  });
  const readAll = useMutation({
    mutationFn: markAllRead,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  useEffect(() => {
    if (!me) return;
    const unsub = realtime.subscribe(`notifications:${me.user.id}`);
    const off = realtime.on((msg) => {
      if (msg.topic === `notifications:${me.user.id}`) {
        void qc.invalidateQueries({ queryKey: ['notifications'] });
      }
    });
    return () => { unsub(); off(); };
  }, [me, qc]);

  const unread = count.data ?? 0;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'relative flex h-9 w-9 items-center justify-center rounded-xl transition-all duration-150',
          open ? 'bg-gray-100 text-gray-700' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700',
        )}
        aria-label="Notifications"
      >
        <Bell className="h-[17px] w-[17px]" />
        {unread > 0 && (
          <span className="absolute right-1.5 top-1.5 flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-amber-500 px-0.5 text-[9px] font-bold text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 z-20 mt-2 w-[360px] overflow-hidden rounded-2xl"
            style={{
              background: '#ffffff',
              border: '1px solid #e8e7e4',
              boxShadow: '0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
            }}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-4 py-3.5"
              style={{ borderBottom: '1px solid #f0efec' }}
            >
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-bold text-gray-900">Notifications</span>
                {unread > 0 && (
                  <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold text-white">
                    {unread}
                  </span>
                )}
              </div>
              {unread > 0 && (
                <button
                  onClick={() => readAll.mutate()}
                  disabled={readAll.isPending}
                  className="flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11px] font-semibold text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
                >
                  <CheckCheck className="h-3 w-3" />
                  Mark all read
                </button>
              )}
            </div>

            {/* List */}
            <div className="max-h-[380px] overflow-y-auto">
              {list.data && list.data.length > 0 ? (
                <div className="divide-y divide-gray-50">
                  {list.data.map((n) => (
                    <NotifItem key={n.id} n={n} />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gray-50">
                    <Bell className="h-4.5 w-4.5 text-gray-300" />
                  </div>
                  <p className="mt-3 text-[12px] font-medium text-gray-400">
                    {list.isLoading ? 'Loading…' : 'All caught up'}
                  </p>
                  {!list.isLoading && (
                    <p className="mt-0.5 text-[11px] text-gray-300">No notifications right now</p>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="flex items-center justify-center gap-1.5 py-3 text-[11.5px] font-semibold text-gray-400 transition-colors hover:text-gray-700"
              style={{ borderTop: '1px solid #f0efec' }}
            >
              View all notifications
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
